import { useMemo } from 'react'
import { Icon } from '@chakra-ui/react'
import { useTranslation } from 'next-i18next'

import { Props as ProductProps } from '@/features/otherServices/components/Product'

import { ReactComponent as LogoCookieWow } from '@/svg/otherServices/logo-cookie-wow.svg'
import { ReactComponent as LogoLearnPDPA } from '@/svg/otherServices/logo-learn-pdpa.svg'
import { ReactComponent as LogoPDPAForm } from '@/svg/otherServices/logo-pdpa-form.svg'
import { ReactComponent as LogoPDPAPro } from '@/svg/otherServices/logo-pdpa-pro.svg'
import { ReactComponent as LogoProkit } from '@/svg/otherServices/logo-pdpa-prokit.svg'

import ImageCookieWow from '@/images/other-services/cookie-wow.png'
import ImagePDPAForm from '@/images/other-services/pdpa-form.png'
import ImageProkit from '@/images/other-services/pdpa-prokit.png'
import ImageLearnPDPA from '@/images/other-services/learn-pdpa.png'
import ImagePDPAPro from '@/images/other-services/pdpa-pro.png'

const scaleFactor = 0.7

export function useCookieWowData(): ProductProps {
  const { t } = useTranslation('otherServices')

  return useMemo(
    () => ({
      id: 'cookie-wow',
      title: 'Cookie Wow',
      bgColor: '#EA834F',
      index: 4,
      logo: (
        <Icon
          as={LogoCookieWow}
          sx={{
            width: { base: `${317 * scaleFactor}px`, md: '317px' },
            height: { base: `${70 * scaleFactor}px`, md: '70px' },
          }}
        />
      ),
      header: t('cookieWow.subtitle'),
      list: [
        t('cookieWow.list.0'),
        t('cookieWow.list.1'),
        t('cookieWow.list.2'),
        t('cookieWow.list.3'),
      ],
      image: ImageCookieWow,
      href: 'https://cookiewow.com/',
    }),
    [t],
  )
}

export function usePDPAFormData(): ProductProps {
  const { t } = useTranslation('otherServices')

  return useMemo(
    () => ({
      id: 'pdpa-form',
      title: 'PDPA Form',
      bgColor: '#2879F9',
      index: 4,
      logo: (
        <Icon
          as={LogoPDPAForm}
          sx={{
            width: { base: `${300 * scaleFactor}px`, md: '300px' },
            height: { base: `${70 * scaleFactor}px`, md: '70px' },
          }}
        />
      ),
      header: t('pdpaForm.subtitle'),
      list: [
        t('pdpaForm.list.0'),
        t('pdpaForm.list.1'),
        t('pdpaForm.list.2'),
        t('pdpaForm.list.3'),
      ],
      image: ImagePDPAForm,
      href: 'https://pdpaform.com/',
    }),
    [t],
  )
}

export function useProkitData(): ProductProps {
  const { t } = useTranslation('otherServices')

  return useMemo(
    () => ({
      id: 'pdpa-prokit',
      title: 'PDPA Prokit',
      bgColor: '#C49252',
      index: 4,
      logo: (
        <Icon
          as={LogoProkit}
          sx={{
            width: { base: `${180 * scaleFactor}px`, md: '180px' },
            height: { base: `${100 * scaleFactor}px`, md: '100px' },
          }}
        />
      ),
      header: t('pdpaProkit.subtitle'),
      list: [
        t('pdpaProkit.list.0'),
        t('pdpaProkit.list.1'),
        t('pdpaProkit.list.2'),
        t('pdpaProkit.list.3'),
      ],
      image: ImageProkit,
      href: 'https://pdpa.pro/prokit',
    }),
    [t],
  )
}

export function useLearnPDPAData(): ProductProps {
  const { t } = useTranslation('otherServices')

  return useMemo(
    () => ({
      id: 'learn-pdpa',
      title: 'LearnPDPA',
      bgColor: '#2879F9',
      index: 4,
      logo: (
        <Icon
          as={LogoLearnPDPA}
          sx={{
            width: { base: `${320 * scaleFactor}px`, md: '320px' },
            height: { base: `${70 * scaleFactor}px`, md: '70px' },
          }}
        />
      ),
      header: t('learnPDPA.subtitle'),
      list: [
        t('learnPDPA.list.0'),
        t('learnPDPA.list.1'),
        t('learnPDPA.list.2'),
        t('learnPDPA.list.3'),
      ],
      image: ImageLearnPDPA,
      href: 'https://learnpdpa.com/',
    }),
    [t],
  )
}

export function usePDPAProData(): ProductProps {
  const { t } = useTranslation('otherServices')

  return useMemo(
    () => ({
      id: 'pdpa-pro',
      title: 'PDPA Pro',
      bgColor: 'linear-gradient(315deg, #00E3AB 0%, #32C4D7 100%)',
      index: 4,
      logo: (
        <Icon
          as={LogoPDPAPro}
          sx={{
            width: { base: `${296 * scaleFactor}px`, md: '296px' },
            height: { base: `${70 * scaleFactor}px`, md: '70px' },
          }}
        />
      ),
      header: t('pdpaPro.subtitle'),
      list: [
        t('pdpaPro.list.0'),
        t('pdpaPro.list.1'),
        t('pdpaPro.list.2'),
        t('pdpaPro.list.3'),
      ],
      image: ImagePDPAPro,
      href: 'https://pdpa.pro/',
    }),
    [t],
  )
}

export function useProducts(): ProductProps[] {
  const COOKIE_WOW = useCookieWowData()
  const PDPA_FORM = usePDPAFormData()
  const PROKIT = useProkitData()
  const LEARN_PDPA = useLearnPDPAData()
  const PDPA_PRO = usePDPAProData()

  return [COOKIE_WOW, PDPA_FORM, PROKIT, LEARN_PDPA, PDPA_PRO]
}
